/**
 * Composing <Route> in React Router v6
 * https://gist.github.com/mjackson/d54b40a094277b7afdd6b81f51a0393f
 *
 * Upgrading from v5
 * https://reactrouter.com/docs/en/v6/upgrading/v5
 */
import { useRef, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";

import { useAuth } from "context/AuthContext";

import { MsalProvider } from "@azure/msal-react";
import { ModalCategorySelection } from "components/ui/ModalCategorySelection";
import Spinner from "components/ui/Spinner";
import { ActivePhases, useActivePhases } from "hooks/useControlDate";
import { LoginBanca } from "pages/Auth/LoginBanca";
import { LoginElbb } from "pages/Auth/LoginElbb";
import { LoginPublic } from "pages/Auth/LoginPublic";
import { CadastroConexao } from "pages/CadastroConexao";
import { CadastroELBB } from "pages/CadastroELBB";
import { ClassificadasEtapa1Elbb } from "pages/ClassificadasEtapa1Elbb";
import { ConfirmacaoElbb } from "pages/ConfirmacaoElbb";
import { ConfirmacaoVotoEtapa1 } from "pages/ConfirmacaoVotoEtapa1";
import { ConfirmacaoVotoEtapa2 } from "pages/ConfirmacaoVotoEtapa2";
import { ConfirmacaoVotoEtapa3 } from "pages/ConfirmacaoVotoEtapa3";
import { HomeElbb } from "pages/HomeElbb";
import { HomePublic } from "pages/HomePublic";
import { IndicadosElbb } from "pages/IndicadosElbb";
import { Iniciativas } from "pages/Iniciativas";
import { IniciativasElbb } from "pages/IniciativasElbb";
import { InscricaoIniciativaElbb } from "pages/InscricaoIniciativaElbb";
import { Listagem } from "pages/Listagem";
import { ListagemInscricaoElbb } from "pages/ListagemInscricaoElbb";
import { MeusVotos } from "pages/MeusVotos";
import { MeusVotosEtapa2 } from "pages/MeusVotosEtapa2";
import { MeusVotosEtapa3 } from "pages/MeusVotosEtapa3";
import { MinhaJornada } from "pages/MinhaJornada";
import { MinhaJornadaElbb } from "pages/MinhaJornadaElbb";
import { MinhasIniciativasElbb } from "pages/MinhasIniciativasElbb";
import { NotFound } from "pages/NotFound";
import { Regulamento } from "pages/Regulamento";
import { ValidacaoIniciativas } from "pages/ValidacaoIniciativas";
import { VotacaoEtapa1 } from "pages/VotacaoEtapa1";
import { VotacaoEtapa2 } from "pages/VotacaoEtapa2";
import { VotacaoEtapa3 } from "pages/VotacaoEtapa3";
import { useEffect } from "react";
import CustomNavigationClient from "sso/azure/CustomNavigationClient";
import { setSelectedCategory as saveSelectedCategory } from "utils/categoryHelpers";
import { MinhasIniciativasElbbEligibleRoute } from "./MinhasIniciativasElbbEligibleRoute";
import { PrivateRoute } from "./PrivateRoute";
import { PublicRoute } from "./PublicRoute";

export const RouteList = ({ msalInstance }: any) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const navigationClient = new CustomNavigationClient(navigate);
  msalInstance.setNavigationClient(navigationClient);

  const InitialRedirect = () => {
    const [isRedirecting, setIsRedirecting] = useState(true);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const hasRedirectedRef = useRef(false);
    const phases = useActivePhases();

    // Detectar outra categoria alem de ELBB
    const userCategory = user?.detail?.category;
    const outraCategoria = Array.isArray(userCategory)
      ? userCategory.find((c: string) => c === "CONEXAO" || c === "JJ_PREDEFINIDO" || c === "JJ_HIBRIDO") || null
      : null;

    useEffect(() => {
      const handleRedirect = async () => {
        if (!user || hasRedirectedRef.current) return;

        hasRedirectedRef.current = true;

        // Banca técnica (etapa 2): vai pra home-elbb
        if (user.extra?.etapa === 2) {
          navigate("/home-elbb");
          setIsRedirecting(false);
          return;
        }

        // Fase votação etapa 1
        if (phases.elbbVotingStage1) {
          navigate("/home-elbb");
          setIsRedirecting(false);
          return;
        }

        // Fase confirmação: gestores validam, funcionários veem listagem
        if (phases.elbbConfirmation) {
          navigate("/home-elbb");
          // navigate(user.detail?.is_manager === true ? "/validacaoIniciativas" : "/listagem");
          // setIsRedirecting(false);
          return;
        }

        // Usuário não habilitado
        if (user.detail?.habilitado === false) {
             navigate("/home-elbb");
          // navigate("/not-found", { state: { reason: "nao-habilitado" } });
          // setIsRedirecting(false);
          return;
        }

        // Fase cadastro conexão: todos vão para home-elbb
        if (phases.conexaoRegistrationPeriod) {
          navigate("/home-elbb");
          setIsRedirecting(false);
          return;
        }

        // Fase pré-inscrição ELBB
        if (phases.elbbPreRegistration) {
          if (user.detail?.is_manager === true) {
            navigate("/not-found", { state: { reason: "is-gestor" } });
            setIsRedirecting(false);
            return;
          }

          try {
            const { buscarEntregas } = await import("services/entrega.service");
            const iniciativas = await buscarEntregas({
              player: user._id,
              start: "2026-01-01T00:00:00-03:00",
            });
            if (iniciativas?.length > 0) {
              navigate("/listagem");
              setIsRedirecting(false);
              return;
            }
          } catch (error) {
            console.error("Erro ao verificar iniciativas:", error);
          }

          navigate("/cadastroElbb");
          setIsRedirecting(false);
          return;
        }

        // Fase inscrição ELBB
        if (phases.elbbInscriptionPeriod) {
          navigate("/home-elbb");
          setIsRedirecting(false);
          return;
        }

        // Buscar iniciativas para fases restantes
        let iniciativas: any[] = [];
        try {
          const { buscarEntregas } = await import("services/entrega.service");
          iniciativas = await buscarEntregas({
            player: user._id,
            start: "2026-01-01T00:00:00-03:00",
          });
        } catch (error) {
          console.error("Erro ao verificar iniciativas:", error);
        }

        // Fase pré-inscrição conexão
        if (phases.conexaoPreRegistration) {
          const userCategory = user?.extra?.category === "JJ_PREDEFINIDO" ? "PILAR" : "CONEXAO";
          const hasIniciativas = iniciativas.some(
            (i: any) => i.owner === user._id && i.categoryPremio === userCategory
          );
          if (hasIniciativas) {
            navigate("/listagem");
          } else {
            const cadastroRoute = user?.extra?.category === "JJ_PREDEFINIDO" ? "/cadastroPilar" : "/cadastroConexao";
            navigate(cadastroRoute);
          }
          setIsRedirecting(false);
          return;
        }

        // Sem fase específica ativa
        if (iniciativas.length > 0) {
          navigate("/home-elbb");
        } else if (phases.canAccessCadastroELBB) {
          navigate("/home-elbb");
        } else {
          navigate("/home-elbb");
        }

        setIsRedirecting(false);
      };

      handleRedirect();
    }, [navigate, user, phases]);

    if (showCategoryModal && outraCategoria) {
      return (
        <ModalCategorySelection
          outraCategoria={outraCategoria}
          onSelectELBB={() => {
            saveSelectedCategory("ELBB");
            setShowCategoryModal(false);
            hasRedirectedRef.current = false;
          }}
          onSelectOutra={() => {
            window.location.href = "https://premio.bb.com";
          }}
        />
      );
    }

    if (isRedirecting) {
      return (
        <div className="flex items-start justify-center w-full h-screen">
          <div className="flex flex-col">
            <Spinner />
            <span className="text-center text-blue-bb">
              Carregando...
            </span>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <MsalProvider instance={msalInstance}>
      <Routes>
        <Route
          path="/"
          element={
            <PrivateRoute redirectTo="/login">
              {!user ? (
                <div className="flex items-start justify-center w-full h-screen">
                  <div className="flex flex-col">
                    <Spinner />
                    <span className="text-center text-blue-bb">
                      Carregando...
                    </span>
                  </div>
                </div>
              ) : (
                <InitialRedirect />
              )}
            </PrivateRoute>
          }
        />

        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginElbb />
            </PublicRoute>
          }
        />

         <Route
          path="/login-public"
          element={
            <PublicRoute>
              <LoginPublic />
            </PublicRoute>
          }
        />

        <Route
          path="/login-elbb"
          element={
            <PublicRoute>
              <LoginElbb />
            </PublicRoute>
          }
        />

        <Route
          path="/login-banca"
          element={
            <PublicRoute>
              <LoginBanca />
            </PublicRoute>
          }
        />

        <Route
          path="/home-public"
          element={
            <PrivateRoute
              redirectTo="/login"
              //requiredPhase={(p: ActivePhases) => p.conexaoRegistrationPeriod || !p.isAnyConexaoActive}
            >
              <HomePublic />
            </PrivateRoute>
          }
        />

        <Route
          path="/home-elbb"
          element={
            <PrivateRoute redirectTo="/login-elbb">
              <HomeElbb />
            </PrivateRoute>
          }
        />

        <Route
          path="/regulamento-elbb"
          element={<Regulamento />}
        />

        <Route
          path="/iniciativas"
          element={
            <PrivateRoute redirectTo="/login">
              <Iniciativas />
            </PrivateRoute>
          }
        />

        <Route
          path="/iniciativas-elbb"
          element={
            <PrivateRoute redirectTo="/login-elbb">
              <IniciativasElbb />
            </PrivateRoute>
          }
        />

        <Route
          path="/indicados-elbb"
          element={
            <PrivateRoute redirectTo="/login-elbb">
              <IndicadosElbb />
            </PrivateRoute>
          }
        />

        <Route
          path="/classificadas-etapa-1-elbb"
          element={
            <PrivateRoute redirectTo="/login-elbb">
              <ClassificadasEtapa1Elbb />
            </PrivateRoute>
          }
        />

        <Route
          path="/minha-jornada"
          element={
            <PrivateRoute redirectTo="/login">
              <MinhaJornada />
            </PrivateRoute>
          }
        />

        <Route
          path="/minha-jornada-elbb"
          element={
            <PrivateRoute redirectTo="/login-elbb">
              <MinhaJornadaElbb />
            </PrivateRoute>
          }
        />

        <Route
          path="/minhas-iniciativas-elbb"
          element={
            <PrivateRoute redirectTo="/login-elbb">
              <MinhasIniciativasElbbEligibleRoute>
                <MinhasIniciativasElbb />
              </MinhasIniciativasElbbEligibleRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/cadastroELBB"
          element={
            <PrivateRoute
              redirectTo="/login"
              requireNonManager={true}
              requiredPhase={(p: ActivePhases) => p.canAccessCadastroELBB}
            >
              <CadastroELBB />
            </PrivateRoute>
          }
        />

        <Route
          path="/cadastroConexao"
          element={
            <PrivateRoute
              redirectTo="/login"
              requireNonManager={false}
              //requiredPhase={(p: ActivePhases) => p.conexaoPreRegistration || p.canAccessCadastroConexao}
            >
              <CadastroConexao />
            </PrivateRoute>
          }
        />

        <Route
          path="/listagem"
          element={
            <PrivateRoute
              redirectTo="/login"
              requireNonManager={true}
              requiredPhase={(p: ActivePhases) => p.conexaoPreRegistration || p.elbbPreRegistration || p.elbbConfirmation || !p.isAnyConexaoActive || p.elbbConfirmation}
            >
              <Listagem />
            </PrivateRoute>
          }
        />

        <Route
          path="/validacaoIniciativas"
          element={
            <PrivateRoute
              redirectTo="/login"
              requireManager={true}
              requiredPhase={(p: ActivePhases) => p.elbbConfirmation}
            >
              <ValidacaoIniciativas />
            </PrivateRoute>
          }
        />

        <Route
          path="/inscricao-iniciativa-elbb/:id"
          element={
            <PrivateRoute redirectTo="/login-elbb">
              <InscricaoIniciativaElbb />
            </PrivateRoute>
          }
        />

        <Route
          path="/listagem-inscricao-elbb"
          element={
            <PrivateRoute redirectTo="/login-elbb">
              {/* <MinhasIniciativasElbbEligibleRoute> */}
                <ListagemInscricaoElbb />
              {/* </MinhasIniciativasElbbEligibleRoute> */}
            </PrivateRoute>
          }
        />

        <Route
          path="/confirmacao-elbb"
          element={
            <PrivateRoute redirectTo="/login-elbb">
              <ConfirmacaoElbb />
            </PrivateRoute>
          }
        />

        <Route
          path="/votacao-etapa-1"
          element={
            <PrivateRoute redirectTo="/login-elbb">
              <VotacaoEtapa1 />
            </PrivateRoute>
          }
        />

        <Route
          path="/meus-votos"
          element={
            <PrivateRoute redirectTo="/login-elbb">
              <MeusVotos />
            </PrivateRoute>
          }
        />

        <Route
          path="/votacao-etapa-2"
          element={
            <PrivateRoute redirectTo="/login-elbb">
              <VotacaoEtapa2 />
            </PrivateRoute>
          }
        />

        <Route
          path="/confirmacao-voto-etapa1"
          element={
            <PrivateRoute redirectTo="/login-elbb">
              <ConfirmacaoVotoEtapa1 />
            </PrivateRoute>
          }
        />

        <Route
          path="/votacao-etapa-2"
          element={
            <PrivateRoute redirectTo="/login" requiredPhase={(p: ActivePhases) => p.elbbVotingStage2}>
              <VotacaoEtapa2 />
            </PrivateRoute>
          }
        />

        <Route
          path="/meus-votos-etapa-2"
          element={
            <PrivateRoute redirectTo="/login">
              <MeusVotosEtapa2 />
            </PrivateRoute>
          }
        />

        <Route
          path="/confirmacao-voto-etapa-2"
          element={
            <PrivateRoute redirectTo="/login" requiredPhase={(p: ActivePhases) => p.elbbVotingStage2}>
              <ConfirmacaoVotoEtapa2 />
            </PrivateRoute>
          }
        />

        <Route
          path="/votacao-etapa-3"
          element={
            <PrivateRoute redirectTo="/login-elbb" requiredPhase={(p: ActivePhases) => p.etapa3premio}>
              <VotacaoEtapa3 />
            </PrivateRoute>
          }
        />

        <Route
          path="/confirmacao-voto-etapa-3"
          element={
            <PrivateRoute redirectTo="/login-elbb" requiredPhase={(p: ActivePhases) => p.etapa3premio}>
              <ConfirmacaoVotoEtapa3 />
            </PrivateRoute>
          }
        />

        <Route
          path="/meus-votos-etapa-3"
          element={
            <PrivateRoute redirectTo="/login-elbb">
              <MeusVotosEtapa3 />
            </PrivateRoute>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </MsalProvider>
  );
};
